import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { calculateDurationLeft, calculatePercentage } from "~/utils/TimerUtils"
import { formattedDuration, formattedWeekLabel } from "~/utils/TimeFormatter"
import { postEvent } from "~/utils/EventManager"
import User from "~/components/User/User"

import "./DashboardTableRow.scss"
import { getUser, switchCycle } from "~/store"
import { Timer } from "~/types"
import { UserWithStats } from "~/utils/DashboardUtils"
import HeatmapWidget from "../HeatmapWidget/HeatmapWidget"
import IconCaretDown from "../Icons/IconCaretDown"
import classNames from "classnames"

type DashboardTableRowProps = {
  index: number
  user?: UserWithStats | null
  timer?: Timer | null
  weekOffset: number
}

function DashboardTableRow(props: DashboardTableRowProps) {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const [heatmapOpen, setHeatmapOpen] = useState(false)
  const [heatmapMounted, setHeatmapMounted] = useState(false)
  const [bgMode, setbgMode] = useState("")
  const [percentage, setPercentage] = useState(0)
  const currentTimer = props.timer ?? null
  const [durationLeft, setDurationLeft] = useState(
    currentTimer ? calculateDurationLeft(currentTimer) : 0,
  )
  const rowUser = props.user ?? null

  const onEnded = () => {
    postEvent({ spaceName: rowUser?.attributes.space_name }, "timerEnded", {
      timer_id: currentTimer?.id,
    })
    if (user?.space_name == rowUser?.attributes.space_name) {
      dispatch(switchCycle())
    }
  }

  useEffect(() => {
    if (currentTimer == null) return
    setbgMode(`DashboardTableRow--${currentTimer.break ? "break" : "work"}`)
    const interval = setInterval(
      (function updateDuration() {
        const durationLeftNow = calculateDurationLeft(currentTimer)
        setPercentage(calculatePercentage(currentTimer))
        setDurationLeft(durationLeftNow)
        if (durationLeftNow == 0) {
          // registerUsersOnTimer()
          onEnded()
        }
        return updateDuration
      })(),
      1000,
    )
    return () => {
      setbgMode("")
      setPercentage(0)
      clearInterval(interval)
      setDurationLeft(0)
    }
  }, [currentTimer])

  if (!rowUser) return null

  const toggleHeatmap = () => {
    // Don't do anything while animating
    if (heatmapOpen !== heatmapMounted) return

    if (heatmapOpen) {
      setHeatmapOpen(false)
      setTimeout(() => setHeatmapMounted(false), 300)
    } else {
      setHeatmapMounted(true)
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setHeatmapOpen(true)),
      )
    }
  }

  return (
    <>
      <div
        className={classNames(
          "DashboardTableRow",
          {
            "DashboardTableRow--open": heatmapOpen,
          },
          bgMode,
        )}
        style={{
          // @ts-expect-error CSS variable
          "--percentage": percentage + "%",
        }}
      >
        <div className="DashboardTableRow__cell">
          <div className="DashboardTableRow__name">
            <User
              key={props.index}
              name={rowUser.attributes.name}
              picture={rowUser.attributes.picture}
              className="DashboardTableRow__picture"
            />
            <span>{rowUser.attributes.name}</span>
          </div>
        </div>
        {props.weekOffset === 0 && (
          <div className="DashboardTableRow__cell">
            <span className="DashboardTableRow__pomodoros">
              {rowUser.today_timers} pomodoros
            </span>
            <span className="DashboardTableRow__description">Today</span>
          </div>
        )}
        <div className="DashboardTableRow__cell">
          <span className="DashboardTableRow__pomodoros">
            {rowUser.week_timers} pomodoros
          </span>
          <span className="DashboardTableRow__description">
            {formattedWeekLabel(props.weekOffset)}
          </span>
        </div>
        {props.weekOffset === 0 && (
          <div className="DashboardTableRow__cell">
            {rowUser.attributes.space_name != "" ? (
              <>
                {currentTimer != null &&
                calculateDurationLeft(currentTimer) > 0 ? (
                  <span className="DashboardTableRow__duration">
                    {formattedDuration(durationLeft)}
                  </span>
                ) : (
                  "- -"
                )}
              </>
            ) : (
              ""
            )}
          </div>
        )}
        <div className="DashboardTableRow__actions">
          <button
            className={classNames("DashboardTableRow__toggle", {
              "DashboardTableRow__toggle--open": heatmapOpen,
            })}
            onClick={toggleHeatmap}
          >
            <IconCaretDown width={20} height={20} />
          </button>
        </div>
      </div>
      {heatmapMounted && (
        <div
          className={classNames("DashboardTableRow__heatmap", {
            "DashboardTableRow__heatmap--open": heatmapOpen,
          })}
        >
          <div className={"DashboardTableRow__heatmap-content"}>
            <div>
              <HeatmapWidget
                user={rowUser.attributes}
                weekOffset={props.weekOffset}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DashboardTableRow
